import * as React from 'react';
import { styled } from '@glitz/react';
import { Button } from 'webapp/components/button';
import { Theme, isNordicNest } from 'webapp/theme';
import { FieldSize } from 'webapp/components/field-size';
import { black, white } from 'webapp/shared/sharedTheme';
import { createFlipYDecorator, createSlideHeightAnimationComponent, useOnClickOutside } from '@avensia/excite-ui';
import Facet from './Facet';
import { ArrowDown } from 'webapp/components/icons';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { toFacetValue } from './BooleanFacet';

type PropType = {
  highlightedFacet: Excite.Esales.FacetApiModel;
  selectedFacets?: Excite.Esales.FacetApiModel[];
  openHighlightedFacet: (facet: Excite.Esales.FacetApiModel) => void;
  onFacetValueChange: (
    updatedFacet: Excite.Esales.FacetApiModel,
    changedValue: Excite.Esales.FacetValueApiModel,
  ) => void;
};

export const DesktopFacetButton = ({
  highlightedFacet,
  selectedFacets,
  openHighlightedFacet,
  onFacetValueChange,
}: PropType) => {
  const [openDrodwon, setOpenDropwdown] = React.useState(false);
  const ref = React.useRef<HTMLLIElement>(null);
  useOnClickOutside(ref, () => setOpenDropwdown(false));

  const handleClick = React.useCallback(
    (facet: Excite.Esales.FacetApiModel) => {
      if (facet.isRange) {
        openHighlightedFacet(facet);
      } else if (facet.values[0]?.text === 'true' || facet.values[0]?.text === 'false') {
        onFacetValueChange(
          {
            ...facet,
            values: [toFacetValue(!facet.values[0]?.selected)],
          },
          toFacetValue(!facet.values[0]?.selected),
        );
      } else {
        setOpenDropwdown(!openDrodwon);
      }
    },
    [onFacetValueChange, openDrodwon, openHighlightedFacet],
  );

  const showMoreFacets = React.useCallback(
    (facet: Excite.Esales.FacetApiModel) => {
      openHighlightedFacet(facet);
      setOpenDropwdown(false);
    },
    [openHighlightedFacet],
  );

  const isBooleanFacet = highlightedFacet.values[0]?.text === 'true' || highlightedFacet.values[0]?.text === 'false';
  return (
    <HighlightedFacet ref={ref}>
      <FacetButton
        as={Theme.White}
        size={FieldSize.Small}
        onClick={() => handleClick(highlightedFacet)}
        css={styled(
          selectedFacets?.find(f => f.attribute === highlightedFacet.attribute) && activeDecorator,
          isBooleanFacet && booleanFacetDecorator,
        )}
        disableRippleEffect
      >
        {highlightedFacet.text}
        {!isBooleanFacet && <ArrowDown css={createFlipYDecorator(openDrodwon)} />}
      </FacetButton>
      <SlowerSlideHeightAnimation visible={openDrodwon}>
        <Facet
          facet={highlightedFacet}
          onFacetValueChange={onFacetValueChange}
          showMoreFacets={showMoreFacets}
          compact
        />
      </SlowerSlideHeightAnimation>
    </HighlightedFacet>
  );
};

const FacetButton = styled(Button, {
  flexShrink: 0,
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: t => (isNordicNest ? t.fontWeight?.bold : t.fontWeight?.normal),
  gap: t => t.sharedSpacing.xxs,
  padding: { left: t => t.sharedSpacing.m, right: t => t.sharedSpacing.s },
  height: 40,
});

const booleanFacetDecorator = styled({
  padding: { right: t => t.sharedSpacing.m },
});

const activeDecorator = styled(
  createButtonHoverDecorator({
    border: { xy: { color: black } },
  }),
  {
    border: { xy: { color: black } },
  },
);

const SlowerSlideHeightAnimation = createSlideHeightAnimationComponent(
  styled.div({
    position: 'absolute',
    top: t => `calc(100% + ${t.sharedSpacing.m}px)`,
    minWidth: 320,
    boxShadow: 'rgba(0, 0, 0, 0.1) 10px 10px 40px 0px',
    backgroundColor: white,
    zIndex: 1,
    border: {
      x: { width: 1, style: 'solid', color: t => t.facets?.search?.borderColor },
      bottom: { width: 1, style: 'solid', color: t => t.facets?.search?.borderColor },
    },
  }),
  { duration: 300 },
);

const HighlightedFacet = styled.li({
  position: 'relative',
  flexShrink: 0,
  ':last-child': {
    marginRight: t => t.sharedSpacing.xs,
  },
});
